import "./InventorySearch.scss"

import React, { FunctionComponent, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { FilterCategory, GroupedControl } from "config/constants"
import { IMultipleEntities, Guid, Lmid, IAppliedFilter } from "@/types"
import ArticleService from "services/ArticleService"
import SelectSearch from "components/Select/SelectSearch"
import { ArticleShort } from "models/ArticleShort"
import SelectSearchOptionArticle from "components/Select/SelectSearchOptionArticle"
import SelectSearchOptionAsset from "components/Select/SelectSearchOptionAsset"
import { tooltipExactSearchFilterAll } from "components/Tooltip/presets/tooltipExactSearch"
import articleSidebars from "stores/articleSidebars"
import assetSidebars from "stores/assetSidebars"
import AssetArticle from "models/AssetArticle"
import useProfitCenters from "hooks/useProfitCenters"
import Switch from "components/Switch/Switch"
import SwitchOption from "components/Switch/SwitchOption"

type SearchScope = "all" | "article" | "asset"

interface IInventorySearchProps {
    onSelect(filter: IAppliedFilter): void
    includeAssets?: boolean
    onlyUserProfitCenters?: boolean
    isOnlyForHolder: boolean
    allowCustomQuery?: boolean
    grouped?: GroupedControl
    menuWidth?: string
    menuAlign?: "left" | "right"
    placeholder?: string
    testIdPrefix?: string
}

const InventorySearch: FunctionComponent<React.PropsWithChildren<IInventorySearchProps>> = ({
    onSelect,
    includeAssets,
    onlyUserProfitCenters,
    isOnlyForHolder,
    allowCustomQuery,
    grouped,
    menuWidth,
    menuAlign,
    placeholder,
    testIdPrefix,
}) => {
    const { t } = useTranslation()
    const { tenantProfitCenters, userProfitCenters } = useProfitCenters()
    const userProfitCenterIds = useMemo(() => userProfitCenters?.items.map(pc => pc.id) ?? [], [userProfitCenters?.items])
    const [forceKeepMenuOpen, setForceKeepMenuOpen] = useState(false)
    const [isSearchActive, setIsSearchActive] = useState(false)
    const [searchScope, setSearchScope] = useState<SearchScope>("all")
    const [searchResetKey, setSearchResetKey] = useState(0)
    const [isSearching, setIsSearching] = useState(false)

    const handleAllFilterChange = (item: ArticleShort | AssetArticle) => {
        if (item instanceof ArticleShort) {
            onArticleSelect(item)
        } else if (item instanceof AssetArticle) {
            onAssetSelect(item.assetId, item.lmid)
        }
    }

    function getSearchResults(criteria?: string): Promise<(IMultipleEntities<any> | null)[]> {
        const withAssets = includeAssets && (searchScope === "all" || searchScope === "asset")
        const withArticles = searchScope === "all" || searchScope === "article"

        const calls: Promise<IMultipleEntities<any> | null>[] = []

        if (withArticles) {
            calls.push(ArticleService.getArticles({ criteria, isOnlyForHolder }))
        }

        if (withAssets) {
            calls.push(ArticleService.getAssetArticles({
                criteria,
                profitCenterIds: onlyUserProfitCenters
                    ? (tenantProfitCenters && tenantProfitCenters.items.length > 0
                        ? [...userProfitCenterIds, null]
                        : undefined
                    )
                    : undefined,
            }))
        }

        return Promise.all(calls)
    }

    async function onArticleSelect(articleShort: ArticleShort) {
        onSelect({
            category: FilterCategory.article,
            value: articleShort.id,
            label: articleShort.articleNo
        })
    }

    function onAssetSelect(assetId: Guid, assetLmid: Lmid) {
        onSelect({
            category: FilterCategory.asset,
            value: assetId,
            label: assetLmid
        })
    }

    function onAssetFolderClick(assetArticle: AssetArticle) {
        onSelect({
            category: FilterCategory.article,
            value: assetArticle.articleId,
            label: assetArticle.articleNo
        })
        setSearchResetKey(k => k + 1)
        setIsSearchActive(false)
    }

    async function onShowArticleDetailsClick(articleShort: ArticleShort) {
        setForceKeepMenuOpen(true)
        articleSidebars.show({
            articleId: articleShort.id,
            onClose: () => {
                setForceKeepMenuOpen(false)
            }
        })
    }

    async function onShowAssetDetailsClick(assetArticle: AssetArticle) {
        setForceKeepMenuOpen(true)
        assetSidebars.show({
            assetId: assetArticle.assetId,
            onClose: () => {
                setForceKeepMenuOpen(false)
            }
        })
    }

    function renderSelectSearchOption(entity: ArticleShort | AssetArticle, search: string) {
        let optionBody

        if (entity instanceof ArticleShort) {
            optionBody = renderSelectSearchOptionArticle(entity, search)
        } else if (entity instanceof AssetArticle) {
            optionBody = renderSelectSearchOptionAsset(entity, search)
        }

        if (!optionBody) {
            return <></>
        }

        return optionBody
    }

    function renderSelectSearchOptionArticle(articleShort: ArticleShort, highlight: string) {
        return <SelectSearchOptionArticle
            article={articleShort}
            onShowDetailsClick={() => onShowArticleDetailsClick(articleShort)}
            highlight={highlight}
        />
    }

    function renderSelectSearchOptionAsset(assetArticle: AssetArticle, highlight: string) {
        return <SelectSearchOptionAsset
            assetArticle={assetArticle}
            onShowDetailsClick={() => onShowAssetDetailsClick(assetArticle)}
            onFolderClick={() => onAssetFolderClick(assetArticle)}
            highlight={highlight}
        />
    }

    const showScopeFilter = includeAssets && (isSearchActive || forceKeepMenuOpen)

    function scopeOptionProps(scope: SearchScope) {
        const isLocked = isSearching && searchScope !== scope
        return {
            disabled: isLocked,
            tooltip: isLocked ? t('common.searching') : undefined,
        }
    }

    return (
        <div className="inventory-search">
            {showScopeFilter && (
                <div
                    className="inventory-search__scope-filter"
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation() }}
                    onTouchStart={e => { e.stopPropagation() }}>
                    <Switch
                        value={searchScope}
                        onSwitch={setSearchScope}
                        theme="square">
                        <SwitchOption value="all" {...scopeOptionProps("all")}>{t('common.all')}</SwitchOption>
                        <SwitchOption value="article" {...scopeOptionProps("article")}>{t('common.article')}</SwitchOption>
                        <SwitchOption value="asset" {...scopeOptionProps("asset")}>{t('common.asset')}</SwitchOption>
                    </Switch>
                </div>
            )}
            <SelectSearch
                key={searchResetKey}
                getOptions={getSearchResults}
                placeholder={placeholder ?? t('common.search')}
                grouped={grouped}
                menuWidth={menuWidth}
                menuAlign={menuAlign}
                testIdPrefix={testIdPrefix}
                onChange={handleAllFilterChange}
                onAdd={(newValue: string) => onSelect({ category: FilterCategory.all, value: newValue })}
                optionTemplate={renderSelectSearchOption}
                isCreatable={allowCustomQuery}
                forceKeepMenuOpen={forceKeepMenuOpen}
                helpTooltip={tooltipExactSearchFilterAll}
                onMenuOpen={() => setIsSearchActive(true)}
                onMenuClose={() => setIsSearchActive(false)}
                extraCacheUniqs={[searchScope]}
                loadOptionsOnMenuOpen={false}
                searchOnlyWithCriteria={true}
                onSearchPendingChange={setIsSearching}
                debounceTimeout={300}
            />
        </div>
    )
}

export default InventorySearch
